import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { caseService, judicialService, api, interventionService } from "@/services/api";
import { mapDbCaseToFrontend, mapDbInterventionToFrontend } from "@/services/mapping";
import ScrollReveal, { ScrollRevealContainer } from "@/components/feature/ScrollReveal";

const KANBAN_COLUMNS = [
  { key: "abierto", label: "Nuevo", color: "bg-secondary-100 border-secondary-300", dot: "bg-secondary-500" },
  { key: "en_evaluacion", label: "En Evaluación", color: "bg-yellow-50 border-yellow-200", dot: "bg-yellow-400" },
  { key: "en_intervencion", label: "En Intervención", color: "bg-primary-50 border-primary-200", dot: "bg-primary-500" },
  { key: "esperando_insumos", label: "Esperando Insumos", color: "bg-orange-50 border-orange-200", dot: "bg-orange-400" },
  { key: "esperando_informe_docente", label: "Esperando Informe", color: "bg-orange-50 border-orange-200", dot: "bg-orange-400" },
  { key: "informe_generado", label: "Informe Generado", color: "bg-accent-50 border-accent-200", dot: "bg-accent-500" },
  { key: "enviado_tribunal", label: "Enviado a Tribunal", color: "bg-green-50 border-green-200", dot: "bg-green-500" },
  { key: "en_seguimiento", label: "En Seguimiento", color: "bg-blue-50 border-blue-200", dot: "bg-blue-500" },
  { key: "cerrado", label: "Cerrado", color: "bg-background-100 border-background-300", dot: "bg-background-400" },
];

function daysBetween(d1: string, d2: string): number {
  return Math.ceil((new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

export default function KanbanPage() {
  const navigate = useNavigate();
  const todayStr = new Date().toISOString().split("T")[0];
  const [draggedCaseId, setDraggedCaseId] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cases, setCases] = useState<any[]>([]);
  const [courtRequests, setCourtRequests] = useState<any[]>([]);
  const [teacherInputs, setTeacherInputs] = useState<any[]>([]);
  const [interventions, setInterventions] = useState<any[]>([]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [casesData, judicialData, inputsData, intsData] = await Promise.all([
        caseService.list(),
        judicialService.list().catch(() => []),
        api.get("/insumos/solicitados").then(res => res.data).catch(() => []),
        interventionService.list().catch(() => [])
      ]);

      if (Array.isArray(casesData)) {
        setCases(casesData.map(mapDbCaseToFrontend));
      }
      if (Array.isArray(judicialData)) {
        setCourtRequests(judicialData);
      }
      if (Array.isArray(inputsData)) {
        setTeacherInputs(inputsData);
      }
      if (Array.isArray(intsData)) {
        setInterventions(intsData.map(mapDbInterventionToFrontend));
      }
    } catch (err: any) {
      console.error("Error loading kanban data:", err);
      setError("Error al cargar datos del tablero: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const casoInterventions = useMemo(() => {
    const map: Record<string, any[]> = {};
    interventions.forEach((i) => {
      if (!map[i.caseId]) map[i.caseId] = [];
      map[i.caseId].push(i);
    });
    return map;
  }, [interventions]);

  const getSemaforo = (caso: any): { color: string; text: string } => {
    if (caso.estado === "cerrado") return { color: "bg-background-400", text: "Cerrado" };
    const inputs = teacherInputs.filter((t) => t.caso_id === caso.id);
    const courts = courtRequests.filter((cr) => cr.caso_id === caso.id && cr.estado !== "respondido");
    const ints = casoInterventions[caso.id] || [];
    const lastInt = ints.length > 0
      ? ints.reduce((latest, curr) => curr.fecha > latest.fecha ? curr : latest, ints[0])
      : null;
    const daysSince = lastInt ? daysBetween(lastInt.fecha, todayStr) : daysBetween(caso.fechaApertura, todayStr);
    
    const hasOverdueInput = inputs.some((t) => t.estado === "vencido" || (t.estado === "pendiente" && new Date(t.plazo_entrega) < new Date(todayStr)));
    const hasOverdueCourt = courts.some((cr) => cr.estado === "vencido" || (cr.estado === "pendiente" && new Date(cr.fecha_vencimiento) < new Date(todayStr)));

    if (hasOverdueInput || hasOverdueCourt) return { color: "bg-red-500", text: "Crítico" };
    if (caso.judicializado && courts.length > 0 && daysSince > 7) return { color: "bg-red-500", text: "Urgente" };
    if (caso.estado === "esperando_insumos" || caso.estado === "esperando_informe_docente") return { color: "bg-yellow-400", text: "Esperando" };
    if (daysSince > 7) return { color: "bg-yellow-400", text: "Revisar" };
    return { color: "bg-green-500", text: "Al día" };
  };

  const handleDragStart = (caseId: string) => {
    setDraggedCaseId(caseId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (newStatus: string) => {
    if (!draggedCaseId) return;
    const caseId = draggedCaseId;
    setDraggedCaseId(null);

    try {
      setCases(prev => prev.map(c => c.id === caseId ? { ...c, estado: newStatus } : c));
      await caseService.transitionStatus(caseId, newStatus);
    } catch (err: any) {
      console.error("Error transitioning case status:", err);
      alert("Error al actualizar estado del caso: " + (err.response?.data?.detail || err.message));
      await loadData();
    }
  };

  const casosPorEstado = useMemo(() => {
    const map: Record<string, any[]> = {};
    KANBAN_COLUMNS.forEach((col) => { map[col.key] = []; });
    cases.forEach((c) => {
      if (map[c.estado]) map[c.estado].push(c);
    });
    return map;
  }, [cases]);

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in-up">
      <ScrollReveal className="scroll-reveal">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Kanban de Casos</h1>
            <p className="text-sm text-foreground-500 mt-0.5">Arrastra los casos entre columnas para cambiar su estado</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/casos")} className="px-4 py-2 text-sm border border-background-200 rounded-xl hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-list-check mr-1.5" />
              Vista Lista
            </button>
            <button onClick={() => navigate("/casos/nuevo")} className="px-4 py-2 text-sm bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line mr-1.5" />
              Nuevo Caso
            </button>
          </div>
        </div>
      </ScrollReveal>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin mr-2" /> Cargando tablero...
        </div>
      ) : (
        <ScrollRevealContainer staggerDelay={80}>
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-3 min-w-[1800px]">
              {KANBAN_COLUMNS.map((col) => {
                const colCases = casosPorEstado[col.key] || [];
                return (
                  <div
                    key={col.key}
                    className={`flex-shrink-0 w-[240px] rounded-2xl border ${col.color} p-3 scroll-reveal`}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(col.key)}
                  >
                    {/* Column header */}
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                        <h3 className="text-sm font-semibold text-foreground-900">{col.label}</h3>
                      </div>
                      <span className="text-xs font-bold text-foreground-500 bg-white/70 px-2 py-0.5 rounded-full">{colCases.length}</span>
                    </div>

                    {/* Cards */}
                    <div className="space-y-2 min-h-[100px]">
                      {colCases.map((caso) => {
                        const semaforo = getSemaforo(caso);
                        const daysRemaining = caso.fechaLimite ? daysBetween(todayStr, caso.fechaLimite) : 999;
                        return (
                          <div
                            key={caso.id}
                            draggable
                            onDragStart={() => handleDragStart(caso.id)}
                            onClick={() => navigate(`/casos/${caso.id}`)}
                            className={`bg-white rounded-xl p-3 border border-background-200/80 cursor-pointer hover:border-primary-300 hover:shadow-sm transition-all duration-200 ${
                              draggedCaseId === caso.id ? "opacity-50 scale-95" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-2.5 h-2.5 rounded-full ${semaforo.color} flex-shrink-0`} title={semaforo.text} />
                              <span className="text-xs font-mono text-foreground-400">{caso.folio.split("-").pop()}</span>
                              {caso.judicializado && (
                                <span className="text-xs text-purple-600 font-medium ml-auto">⚖</span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-foreground-900 mb-1 line-clamp-2">{caso.studentName}</p>
                            <p className="text-xs text-foreground-500 mb-2 line-clamp-2">{caso.titulo}</p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400">
                              <span>{caso.curso}</span>
                              <span>·</span>
                              <span>{caso.responsableName?.split(" ")[0] || "—"}</span>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-background-100">
                              <span className={`text-xs font-medium ${
                                daysRemaining === 999 ? "text-foreground-400" : daysRemaining <= 0 ? "text-red-600" : daysRemaining <= 3 ? "text-orange-500" : "text-foreground-400"
                              }`}>
                                {daysRemaining === 999 ? "Sin límite" : daysRemaining <= 0 ? "Vencido" : `${daysRemaining}d`}
                              </span>
                              <span className={`text-xs font-medium ${
                                caso.prioridad === "critica" ? "text-red-600" : caso.prioridad === "alta" ? "text-orange-500" : "text-foreground-400"
                              }`}>
                                {caso.prioridad === "critica" ? "CRÍTICA" : caso.prioridad === "alta" ? "ALTA" : ""}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {colCases.length === 0 && (
                        <div className="text-center py-6 text-foreground-300">
                          <p className="text-xs">Sin casos</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollRevealContainer>
      )}

      {/* Legend */}
      <ScrollReveal className="scroll-reveal">
        <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 bg-white border border-background-200/80 rounded-xl p-3">
          <span className="font-semibold text-foreground-700">Semáforo:</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Al día</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /> Requiere atención</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Crítico / Vencido</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-background-400" /> Cerrado</span>
        </div>
      </ScrollReveal>
    </div>
  );
}